/**
 * /tools/kpi-correlation?clientId=X — STP-039.
 *
 * Client-facing view of Pearson r between fulfillment_score and each
 * KPI series (Energy / Work / Love) over a configurable window. The
 * coach sees a richer dashboard via macOS DSH-061; this surface is
 * for the client to read their own pattern.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { PrintButton } from '@/components/print-button';

type WindowDays = 7 | 30 | 90;

export function KpiCorrelationPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [windowDays, setWindowDays] = useState<WindowDays>(30);
  const [data, setData] = useState<api.ClientCorrelation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const result = await api.getClientCorrelation(accessToken, clientId, windowDays);
        if (!active) return;
        setData(result);
      } catch {
        if (!active) return;
        setError(t('tools.kpiCorrelation.errors.generic'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, windowDays, t]);

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="kpi-correlation-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.kpiCorrelation.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.kpiCorrelation.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.kpiCorrelation.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.kpiCorrelation.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.kpiCorrelation.description')}
        </p>
      </header>

      <div role="radiogroup" aria-label={t('tools.kpiCorrelation.windowLabel')} className="flex gap-s2 mb-s5">
        {([7, 30, 90] as WindowDays[]).map((days) => (
          <button
            key={days}
            type="button"
            role="radio"
            aria-checked={windowDays === days}
            onClick={() => setWindowDays(days)}
            data-testid={`kpi-correlation-window-${days}`}
            className={
              windowDays === days
                ? 'px-s4 py-s2 rounded-md bg-flame text-bone font-medium'
                : 'px-s4 py-s2 rounded-md border border-bone-3 text-gray-2 hover:text-ink'
            }
          >
            {t('tools.kpiCorrelation.windowOption', { days })}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-gray-2" data-testid="kpi-correlation-loading">
          {t('tools.kpiCorrelation.loading')}
        </p>
      )}

      {error && (
        <p role="alert" className="form-error" data-testid="kpi-correlation-error">
          {error}
        </p>
      )}

      {data && !loading && !error && (
        <section className="space-y-s4" data-testid="kpi-correlation-results">
          {(['energy', 'work', 'love'] as const).map((axis) => {
            const point = data[axis];
            return (
              <article
                key={axis}
                className="rounded-lg border border-bone-3 bg-bone-2 p-s5"
                data-testid={`kpi-correlation-${axis}`}
              >
                <div className="flex items-baseline justify-between gap-s3">
                  <h2 className="font-display text-display-sm text-ink">
                    {t(`tools.kpiCorrelation.axes.${axis}`)}
                  </h2>
                  <span className="font-mono text-base text-ink tabular-nums">
                    {point.r === null ? '—' : point.r.toFixed(2)}
                  </span>
                </div>
                <p className="mt-s2 text-xs text-gray-1">
                  {t('tools.kpiCorrelation.pairs', { n: point.n })}
                </p>
                <p className="mt-s2 text-gray-2 italic">{interpret(point.r, t)}</p>
              </article>
            );
          })}
          <p className="text-xs text-gray-1 mt-s4">
            {t('tools.kpiCorrelation.minPairsNote')}
          </p>
        </section>
      )}
    </section>
  );
}

function interpret(
  r: number | null,
  t: (key: string) => string,
): string {
  if (r === null) return t('tools.kpiCorrelation.interpret.notEnough');
  if (r >= 0.5) return t('tools.kpiCorrelation.interpret.strongPositive');
  if (r >= 0.2) return t('tools.kpiCorrelation.interpret.moderatePositive');
  if (r <= -0.2) return t('tools.kpiCorrelation.interpret.inverse');
  return t('tools.kpiCorrelation.interpret.flat');
}
